import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import Button from '@repo/ui/button'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { z } from 'zod'

import useMultipleSchema from '../../../../lib/hooks/use-multiple-schema'
import { EnrollmentHealthInput } from '../../../../lib/types/models/enrollment/enrollment-health'
import { NullableTransformer } from '../../../../lib/types/utils'
import { createDateStringSchema } from '../../../../lib/utils/validations/date.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import Form, { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'

type EnrollmentMedicalStatementFormProps = {
  defaultValues?: EnrollmentHealthInput
  enrollmentId: number
  disabled?: boolean
  handleSubmit: (data: EnrollmentHealthInput) => Promise<void>
}

const saveForLaterSchema: z.ZodType<NullableTransformer<EnrollmentHealthInput>> = z.object({
  diphtheria: createDateStringSchema({ inputName: 'Diphtérie' }).nullish(),
  tetanus: createDateStringSchema({ inputName: 'Tétanos' }).nullish(),
  poliomyelitis: createDateStringSchema({ inputName: 'Poliomyélite' }).nullish(),
  dtPolioTetracoq: createDateStringSchema({ inputName: 'DT Polio ou Tétracoq' }).nullish(),
  pertussis: createDateStringSchema({ inputName: 'Coqueluche' }).nullish(),
  hepatitisB: createDateStringSchema({ inputName: 'Hépatite B' }).nullish(),
  rubellaMumpsMeasles: createDateStringSchema({
    inputName: 'Rubéole - Oreillons - Rougeole'
  }).nullish(),
  bcg: createDateStringSchema({ inputName: 'BCG' }).nullish(),
  haemophilus: createDateStringSchema({ inputName: 'Haemophilus' }).nullish(),
  pneumococcus: createDateStringSchema({ inputName: 'Pneumocoque' }).nullish(),
  meningococcusC: createDateStringSchema({ inputName: 'Méningocoque C' }).nullish(),

  otherVaccinations: createStringSchema('Autres vaccins').nullish(),
  additionalRecommendations: createStringSchema('Recommandations utiles des parents', {
    nullish: true
  })
})

const submissionSchema: z.ZodType<NullableTransformer<EnrollmentHealthInput>> = z.object({
  diphtheria: createDateStringSchema({ inputName: 'Diphtérie' }).nullish(),
  tetanus: createDateStringSchema({ inputName: 'Tétanos' }).nullish(),
  poliomyelitis: createDateStringSchema({ inputName: 'Poliomyélite' }).nullish(),
  dtPolioTetracoq: createDateStringSchema({ inputName: 'DT Polio ou Tétracoq :' }).nullish(),

  pertussis: createDateStringSchema({ inputName: 'Coqueluche' }).nullish(),
  hepatitisB: createDateStringSchema({ inputName: 'Hépatite B' }).nullish(),
  rubellaMumpsMeasles: createDateStringSchema({
    inputName: 'Rubéole - Oreillons - Rougeole'
  }).nullish(),
  bcg: createDateStringSchema({ inputName: 'BCG' }).nullish(),
  haemophilus: createDateStringSchema({ inputName: 'Haemophilus' }).nullish(),
  pneumococcus: createDateStringSchema({ inputName: 'Pneumocoque' }).nullish(),
  meningococcusC: createDateStringSchema({ inputName: 'Méningocoque C' }).nullish(),

  otherVaccinations: createStringSchema('Autres vaccins', { nullish: true }),
  additionalRecommendations: createStringSchema('Recommandations utiles des parents', {
    nullish: true
  })
})

export function EnrollmentMedicalStatementForm({
  disabled,
  defaultValues,
  handleSubmit
}: EnrollmentMedicalStatementFormProps) {
  const { form, loading, submitForm } = useMultipleSchema<
    ['save-for-later', 'submit'],
    EnrollmentHealthInput
  >(
    [
      { name: 'save-for-later', schema: saveForLaterSchema },
      { name: 'submit', schema: submissionSchema }
    ],
    async (values, name) =>
      handleSubmit({ ...values, childHealthPapersSubmitted: name === 'submit' }),
    defaultValues
  )
  const additionalRecommendations = form.watch('additionalRecommendations')

  return (
    <Form form={form}>
      <form>
        <div className={'flex flex-col gap-3'}>
          <InnerBox title={'Vaccins obligatoires'} className={'rounded-lg'}>
            <Typography variant={'label'} color={tailwindColors.grey.DEFAULT}>
              Merci de préciser pour chaque vaccin la date du dernier rappel.
            </Typography>

            <div className={'grid mobile:grid-cols-1 grid-cols-2 gap-4 my-4'}>
              <FormField
                control={form.control}
                name={'diphtheria'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Diphtérie'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'tetanus'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Tétanos'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'poliomyelitis'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Poliomyélite'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'dtPolioTetracoq'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'OU DT Polio / Tétracoq'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <Typography variant={'label-medium'} color={tailwindColors.grey.lighter}>
              Si l'enfant n'est pas vacciné, merci de préparer une attestation de contre-indication
              à remettre au responsable du séjour.
            </Typography>
          </InnerBox>

          <InnerBox title={'Vaccins recommandés'} className={'rounded-lg'}>
            <Typography variant={'label'} color={tailwindColors.grey.DEFAULT}>
              Merci de préciser pour chaque vaccin la date du dernier rappel.
            </Typography>

            <div className={'grid mobile:grid-cols-1 grid-cols-2 gap-4 mt-4'}>
              <FormField
                control={form.control}
                name={'pertussis'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Coqueluche'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'hepatitisB'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Hépatite B'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'rubellaMumpsMeasles'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Rubéole - Oreillons - Rougeole'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'bcg'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'BCG'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'haemophilus'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Haemophilus'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'pneumococcus'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Pneumocoque'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'meningococcusC'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Méningocoque C'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'otherVaccinations'}
                render={({ field }) => (
                  <FormItem className={'col-span-2 mobile:col-span-1'}>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type={'text'}
                        label={'Autres vaccinations (préciser)'}
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </InnerBox>

          <Typography variant={'sub-heading'}>Recommandations utiles des parents</Typography>

          <InnerBox className={'rounded-lg'}>
            <Typography variant={'label-semibold'}>
              Voici les informations de santé que vous nous avez communiquées lors de votre demande
              d'inscription :
            </Typography>

            <Typography variant={'label-semibold'} className={'mt-2 whitespace-pre-line'}>
              {valueRender(additionalRecommendations)}
            </Typography>
          </InnerBox>
        </div>

        <div className="flex justify-end mt-4 gap-2.5 mobile:flex-col mobile:gap-2">
          <Button
            label="Enregistrer et continuer plus tard"
            variant="ghost"
            type="button"
            loading={loading['save-for-later']}
            onClick={() => {
              submitForm('save-for-later')
            }}
          />
          <Button
            label="Valider et envoyer à LEC"
            variant="primary"
            type="button"
            loading={loading.submit}
            onClick={() => {
              submitForm('submit')
            }}
          />
        </div>
      </form>
    </Form>
  )
}
